import {
  HANDOVER_KIND_LABELS,
  keyTaken,
  METER_UNITS,
  meterOffersSomething,
  meterTaken,
  type PreviousValues,
  takeAllKeys,
  takeAllMeters,
  takeKey,
  takeMeter,
} from '@acasa/shared'
import { newId, type SectionProps } from './body-utils'
import { usePreviousValues } from './use-handovers'

/**
 * "Aus dem letzten Protokoll": the readings or the keys the last completed
 * protocol for this object recorded, each one a tap away.
 *
 * Offered, never applied. The block sits ABOVE the rows it fills so the agent
 * sees what is on offer before typing — and everything taken lands in the
 * ordinary fields, where it can be corrected like anything typed by hand.
 *
 * The rules of what a tap does live in `@acasa/shared` (`handover-memory.ts`),
 * where they are tested: one row overwrites, "Alle übernehmen" fills blanks.
 */

function describeSource(source: NonNullable<PreviousValues['source']>): string {
  const date = new Date(source.recordedAt).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  return `${HANDOVER_KIND_LABELS[source.kind]} vom ${date}`
}

function Shell({
  source,
  onTakeAll,
  allTaken,
  children,
}: {
  source: NonNullable<PreviousValues['source']>
  onTakeAll: () => void
  allTaken: boolean
  children: React.ReactNode
}) {
  return (
    <section
      aria-label="Werte aus dem letzten Protokoll"
      className="border-brand/30 bg-brand-soft/40 border p-3 sm:p-4"
    >
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h4 className="text-ink text-sm font-medium">Aus dem letzten Protokoll</h4>
        <span className="text-muted text-xs">{describeSource(source)}</span>
      </div>

      <ul className="divide-line divide-y">{children}</ul>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          disabled={allTaken}
          onClick={onTakeAll}
          className="border-line text-muted hover:border-brand hover:text-brand bg-surface min-h-11 border px-4 text-xs transition-colors disabled:opacity-40"
        >
          Alle übernehmen
        </button>
        <p className="text-faint text-[11px] leading-relaxed">
          Füllt nur leere Felder. Jeder übernommene Wert bleibt änderbar.
        </p>
      </div>
    </section>
  )
}

function TakeButton({
  taken,
  label,
  onClick,
}: {
  taken: boolean
  label: string
  onClick: () => void
}) {
  if (taken) return <span className="text-positive shrink-0 text-xs">übernommen</span>
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="border-line text-muted hover:border-brand hover:text-brand bg-surface min-h-11 shrink-0 border px-4 text-xs transition-colors"
    >
      Übernehmen
    </button>
  )
}

/**
 * Drafts only. While a completed protocol is being changed the "last
 * completed protocol" for the object is usually this very one, and offering
 * its own frozen values back to it is noise.
 */
function offersPrevious(protocol: SectionProps['protocol'], readOnly: boolean) {
  return !readOnly && protocol.status === 'draft'
}

export function PreviousMeters({ protocol, body, patch, readOnly }: SectionProps) {
  const query = usePreviousValues(protocol.id, offersPrevious(protocol, readOnly))
  const previous = query.data
  // A row the last protocol left blank has nothing to offer — and "Übernehmen"
  // on it would wipe whatever has been typed into its row since.
  const meters = previous?.meters.filter(meterOffersSomething) ?? []
  if (!offersPrevious(protocol, readOnly) || !previous?.source || meters.length === 0) return null

  const { source } = previous
  // The whole list goes along with every match: a Zählernummer that several of
  // these meters share identifies none of them (see `matchMeter`).
  const allTaken = meters.every((entry) => meterTaken(entry, body.meters, meters))

  return (
    <Shell
      source={source}
      allTaken={allTaken}
      onTakeAll={() =>
        patch((current) => ({ ...current, meters: takeAllMeters(current.meters, meters, newId) }))
      }
    >
      {meters.map((entry) => {
        const name = entry.label || 'Zähler'
        const unit = entry.unit || METER_UNITS[entry.kind]
        return (
          <li
            key={entry.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2 first:pt-0 last:pb-0"
          >
            <div className="min-w-0 flex-1 basis-40">
              <p className="text-ink text-sm">{name}</p>
              <p className="text-muted font-mono text-xs break-words">
                {entry.serial ? `Nr. ${entry.serial}` : 'ohne Nummer'}
                {' · '}
                {entry.value ? `${entry.value}${unit ? ` ${unit}` : ''}` : 'kein Stand'}
              </p>
            </div>
            <TakeButton
              taken={meterTaken(entry, body.meters, meters)}
              label={`${name} übernehmen`}
              onClick={() =>
                patch((current) => ({
                  ...current,
                  meters: takeMeter(current.meters, entry, newId, meters),
                }))
              }
            />
          </li>
        )
      })}
    </Shell>
  )
}

export function PreviousKeys({ protocol, body, patch, readOnly }: SectionProps) {
  const query = usePreviousValues(protocol.id, offersPrevious(protocol, readOnly))
  const previous = query.data
  if (!offersPrevious(protocol, readOnly) || !previous?.source || previous.keys.length === 0)
    return null

  const { source, keys } = previous
  const allTaken = keys.every((entry) => keyTaken(entry, body.keys))

  return (
    <Shell
      source={source}
      allTaken={allTaken}
      onTakeAll={() =>
        patch((current) => ({ ...current, keys: takeAllKeys(current.keys, keys, newId) }))
      }
    >
      {keys.map((entry) => {
        const name = entry.label || 'Schlüssel'
        return (
          <li
            key={entry.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2 first:pt-0 last:pb-0"
          >
            <p className="text-ink min-w-0 flex-1 basis-40 text-sm">
              {name}
              <span className="text-muted tnum ml-2 font-mono text-xs">{entry.count} Stück</span>
            </p>
            <TakeButton
              taken={keyTaken(entry, body.keys)}
              label={`${name} übernehmen`}
              onClick={() =>
                patch((current) => ({ ...current, keys: takeKey(current.keys, entry, newId) }))
              }
            />
          </li>
        )
      })}
    </Shell>
  )
}
